const PLANET_ORDER = ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN', 'RAHU', 'KETU'];

const PLANET_NAME: Record<string, string> = {
  SUN: 'Sun',
  MOON: 'Moon',
  MARS: 'Mars',
  MERCURY: 'Mercury',
  JUPITER: 'Jupiter',
  VENUS: 'Venus',
  SATURN: 'Saturn',
  RAHU: 'Rahu',
  KETU: 'Ketu',
};

type ChakraPlanetPayload = {
  name: string;
  sign: string;
  house: number | string | null;
  degree: number;
  isOwnSign: boolean;
  isExalted: boolean;
  isDebilitated: boolean;
  isRetrograde: boolean;
  isCombust: boolean;
};

const SIGN_LORD: Record<string, string> = {
  Aries: 'Mars',
  Taurus: 'Venus',
  Gemini: 'Mercury',
  Cancer: 'Moon',
  Leo: 'Sun',
  Virgo: 'Mercury',
  Libra: 'Venus',
  Scorpio: 'Mars',
  Sagittarius: 'Jupiter',
  Capricorn: 'Saturn',
  Aquarius: 'Saturn',
  Pisces: 'Jupiter',
};

const getSignName = (sign: any) => {
  if (!sign) return '';
  if (typeof sign === 'string') return sign;
  return sign.name || '';
};

const getDegree = (planet: any) => {
  const value = planet?.degree?.degrees ?? planet?.degree ?? planet?.longitude;
  if (typeof value !== 'number') return 0;
  return value > 30 ? Number((value % 30).toFixed(4)) : value;
};

const normalizePlanetName = (key: string, planet: any) => {
  const raw = String(planet?.name || key).toUpperCase();
  return PLANET_NAME[raw] || PLANET_NAME[key] || planet?.name || key;
};

const getAtmakaraka = (planets: any[]) => {
  const candidates = planets.filter((planet) => !['Rahu', 'Ketu'].includes(planet.name));
  return [...candidates].sort((a, b) => Number(b.degree || 0) - Number(a.degree || 0))[0]?.name || null;
};

export function buildChakraPayload(chart: any) {
  const planets = PLANET_ORDER
    .map((key) => {
      const planet = chart?.planets?.[key] || chart?.planets?.[PLANET_NAME[key]];
      if (!planet) return null;
      const name = normalizePlanetName(key, planet);
      const sign = getSignName(planet.sign);
      return {
        name,
        sign,
        house: planet.house || planet.bhavaChalitHouse || null,
        degree: getDegree(planet),
        isOwnSign: Boolean(planet.isOwnSign || planet.status === 'Own Sign' || SIGN_LORD[sign] === name),
        isExalted: Boolean(planet.isExalted || planet.status === 'Exalted'),
        isDebilitated: Boolean(planet.isDebilitated || planet.status === 'Debilitated'),
        isRetrograde: Boolean(planet.isRetrograde || planet.retrograde),
        isCombust: Boolean(planet.isCombust || planet.combust),
      };
    })
    .filter((planet): planet is ChakraPlanetPayload => Boolean(planet));

  const ascendantSign = getSignName(chart?.houses?.ascendant?.sign);
  const moon = planets.find((planet: any) => planet.name === 'Moon') as any;
  const chartContext = {
    ascendantSign,
    lagnaLord: SIGN_LORD[ascendantSign] || null,
    moonSign: moon?.sign || null,
    moonSignLord: moon?.sign ? SIGN_LORD[moon.sign] || null : null,
    atmakaraka: getAtmakaraka(planets),
  };

  return { planets, chartContext };
}
